// Generated from shared/ by tools/sync-shared.sh. Don't edit; edit shared/ and run it again.
// shared/nowish.json as a typed module (docs/SHARED.md; docs/ARCHITECTURE.md §2.4): bundled, never fetched.

export interface Shared {
  /** Three colours with one meaning each (docs/SPEC.md 'Colours'): accent (blue; accentFill its very light fill), now (green: happening now; nowFill is its very light fill, nowText the darker shade for text), soon (blue: starting within pins.soonMinutes; soonFill its very light fill, soonText the darker shade for text, ≥4.5:1 on white), starred (gold: icons and fills; text on it or on its tint is dark), danger (red: warnings like 'End time unknown'; dangerFill its light-red highlight), plus neutrals. 'dark' = the iOS dark-mode value. */
  readonly colors: {
    readonly accent: {
      readonly light: string;
      readonly dark: string;
    };
    readonly accentFill: {
      readonly light: string;
      readonly dark: string;
    };
    readonly now: {
      readonly light: string;
    };
    readonly nowFill: {
      readonly light: string;
    };
    readonly nowText: {
      readonly light: string;
      readonly dark: string;
    };
    readonly soon: {
      readonly light: string;
    };
    readonly soonFill: {
      readonly light: string;
    };
    readonly soonText: {
      readonly light: string;
      readonly dark: string;
    };
    readonly starred: {
      readonly light: string;
    };
    readonly selected: {
      readonly light: string;
      readonly dark: string;
    };
    readonly text: {
      readonly light: string;
      readonly dark: string;
    };
    readonly muted: {
      readonly light: string;
      readonly dark: string;
    };
    readonly fill: {
      readonly light: string;
      readonly dark: string;
    };
    readonly line: {
      readonly light: string;
      readonly dark: string;
    };
    readonly lineSoft: {
      readonly light: string;
      readonly dark: string;
    };
    readonly surface: {
      readonly light: string;
      readonly dark: string;
    };
    readonly onColor: {
      readonly light: string;
    };
    readonly ink: {
      readonly light: string;
    };
    readonly danger: {
      readonly light: string;
      readonly dark: string;
    };
    readonly dangerFill: {
      readonly light: string;
      readonly dark: string;
    };
    readonly warning: {
      readonly light: string;
      readonly dark: string;
    };
  };
  readonly endOfDayHour: number;
  /** Events whose end nobody knows (the API's `end` is null: no end from the source, and no clue to the length in its text or kind) last `hours` from their start. Their pin and off-screen pointer stay fully opaque for `fadeAfterHours`, then fade linearly to `minOpacity` at `hours` ('ghosting out'); the card says 'End time unknown' (copy card.endUnknown) in colors.danger on colors.dangerFill. The pinOpacity rule, shared/cases/end-fade.json. An end the server worked out from clues (`end_inferred`) counts as known: no fade, no notice. */
  readonly unknownEnd: {
    readonly hours: number;
    readonly fadeAfterHours: number;
    readonly minOpacity: number;
  };
  /** The time tabs (docs/SPEC.md item 3; shared/cases/tabs.json). Days run 3am to 3am (endOfDayHour), so 1am Saturday is still Friday night. No reach = the rest of today. `week`: logical weekdays 1 = Monday … 7 = Sunday; from `from` through the night of `to`, starting now on days `from`…`currentUntil`, else the next such stretch (this week's if it's still ahead, otherwise next week's). `days`: now through that many days ahead. `empty` fills "Nothing here {phrase}"; `emptyAhead`, when given, is used while the tab's stretch starts later. `order`: the tab order by logical weekday. The app opens on the first tab (Today) every time. */
  readonly tabs: readonly {
    readonly id: string;
    readonly title: string;
    readonly empty: string;
    readonly emptyAhead?: string;
    readonly week?: {
      readonly from: number;
      readonly to: number;
      readonly currentUntil: number;
    };
    readonly days?: number;
  }[];
  readonly tabOrder: readonly {
    readonly weekdays: readonly number[];
    readonly tabs: readonly string[];
  }[];
  readonly freeLengths: readonly {
    readonly id: string;
    readonly title: string;
    readonly hours: number | null;
  }[];
  readonly freeDefaultLength: string;
  readonly durations: readonly {
    readonly title: string;
    readonly minutes: number | null;
  }[];
  readonly defaultDuration: number;
  /** The Tabler icon before 'Reset' on the Reset pills (the price slider's and the types list's). */
  readonly resetIcon: string;
  /** The icon before 'Types' on the types filter button, shown only while no types are picked. It sits in the leading slot like the Free gift and price ticket, drawn to their size and weight, centred on the capitals. */
  readonly anyTypeIcon: string;
  /** The price slider: up to `ceiling` (at the top = no limit; the button reads 'Price') in steps of `step`. It closes by itself autoCloseMs after you let go of it (web: or after the last arrow-key change); touching anything in it before then waits again. */
  readonly price: {
    readonly ceiling: number;
    readonly step: number;
    readonly autoCloseMs: number;
  };
  readonly pins: {
    readonly size: {
      readonly interest: number;
      readonly none: number;
    };
    readonly soonMinutes: number;
    /** The time tone (the timeTone rule; shared/cases/time-label.json): happening now = a colors.nowFill fill and a colors.now border outline.nowWidth wide; starting within soonMinutes = colors.soonFill and a colors.soon border outline.soonWidth wide; both follow the pin's contour (notches included). Later: white (colors.surface) with a colors.surface border outline.plainWidth wide. The time text takes the same tone (colors.nowText / soonText) and, when soon, is a countdown. */
    readonly outline: {
      readonly nowWidth: number;
      readonly soonWidth: number;
      readonly plainWidth: number;
    };
    readonly squareCorner: number;
    readonly notchRadius: number;
    /** A paid pin's price (the pinPrice rule; shared/cases/price_label.json) on a little label at its bottom-right corner, overlapping it like a badge: text font px in the color token on the background token, height tall with padX each side and fully round ends; its bottom and right edges overhang past the pin's corner. Only on the selected (enlarged) pin and on list rows' pin icons; never on ordinary map pins, groups or off-screen pointers. */
    readonly price: {
      readonly font: number;
      readonly height: number;
      readonly padX: number;
      readonly overhang: number;
      readonly color: string;
      readonly background: string;
    };
    /** Faded pins for later search matches. */
    readonly secondaryOpacity: number;
    /** Off-screen pointers: the pin's own shape and look at radius (half the size), plus a small chevron chevronGap past its edge pointing at the event (angled in corners): arms chevronSize long, chevronWidth thick, in the colour token chevronColor gives for the pin's outline (the pointerChevron rule; shared/cases/pin-style.json): green (colors.now) when happening, blue (colors.soon) when soon, else grey (colors.muted). */
    readonly pointer: {
      readonly radius: number;
      readonly chevronGap: number;
      readonly chevronSize: number;
      readonly chevronWidth: number;
      readonly chevronColor: {
        readonly now: string;
        readonly soon: string;
        readonly plain: string;
      };
    };
    /** Tapping a pin centres the map on it and it grows to selectScale with a short spring (springMs); moving the map deselects it. Tapping a group centres on it, zooms in one step and its pins pop out to their places over popMs. */
    readonly tap: {
      readonly selectScale: number;
      readonly springMs: number;
      readonly popMs: number;
    };
  };
  readonly group: {
    readonly scale: number;
    readonly maxEmojis: number;
    /** Emoji size and offsets from the centre for 1, 2 and 3 emojis (one in the middle, a diagonal pair, a triangle) in an interest-sized group; a smaller group scales them down with its size. */
    readonly layout: readonly {
      readonly font: number;
      readonly offsets: readonly (readonly number[])[];
    }[];
  };
  /** Corner radius of cards (the nearby list's cards, the event card, toasts, popovers), in points; a hovered list row takes it too. Web: --radius (web/tokens.css, generated); iOS: Theme.cardRadius. */
  readonly cardRadius: number;
  /** Desktop only (a mouse can hover; docs/PARITY.md). Hovering a pin, a group or an event's card in the side panel: the pin (or the group holding it) grows to pinScale at full strength, the card (all of a group's) gets an outline in its time tone (colors.now when happening, else colors.soon) and scrolls into view if it's off screen (from the map only), and every other pin, group, off-screen pointer and card goes to dimOthers of its usual opacity. Changes take ms. */
  readonly hover: {
    readonly pinScale: number;
    readonly dimOthers: number;
    readonly ms: number;
  };
  readonly maxPointers: number;
  /** Off-screen pointers only show where nothing else is: one that would come within this many points of a pin, group, the location dot or a control hides. They fade in and out over pointerFadeMs. */
  readonly pointerClearance: number;
  readonly pointerFadeMs: number;
  /** A filter button that's on (Free; the price with a price set; the types with any picked, or All interests): outlined, filled and written in these colour tokens, the same for all three. Web: filterChip (controls.js, --chip-on-*); iOS: FilterChip. */
  readonly chipOn: {
    readonly border: string;
    readonly fill: string;
    readonly text: string;
  };
  /** Icons whose drawing sits below the middle of their box, in icon units (24 a side): the free gift and price ticket are drawn 1.5 low to leave the gift's bow room, and the types grid shares their box. Filter buttons line every icon up with the middle of the text's capital letters, moving these up by that much (scaled to the icon's size) so their body, not their box, is centred. */
  readonly iconInkDrop: {
    readonly "free-gift": number;
    readonly "price-ticket": number;
    readonly "types-grid": number;
  };
  /** How both apps fetch events (docs/MAP.md 'Fetching events'; shared/cases/fetch-area.json). On camera idle, when the visible area isn't inside an area already fetched, fetch the view grown by marginFactor of its size on every side (at least minSpanDegrees wide and tall, so a street-level view loads its town), from pastHours ago to the widest tab's reach plus aheadHours, in pages of pageLimit (following `next`) up to maxEvents, and merge. Every refreshMinutes (and on coming back to the app) ask only for what changed (`updated_since`); every fullMinutes, or when the widest tab reaches past what was fetched, fetch the view afresh. */
  readonly fetch: {
    readonly marginFactor: number;
    readonly minSpanDegrees: number;
    readonly pastHours: number;
    readonly aheadHours: number;
    readonly pageLimit: number;
    readonly maxEvents: number;
    readonly refreshMinutes: number;
    readonly fullMinutes: number;
  };
  /** The list sheet when nothing is selected (and search results) shows as cards (the flyer above the emoji, title and time, as the desktop's side panel always did) or as a compact list. A two-part toggle at the top right of its header switches between them: `options` in order, left to right, each with its Tabler icon and its label (copy key, spoken by screen readers). The first is the default; each device remembers the choice under `storageKey` (web localStorage, iOS UserDefaults). Collapsed at phone size the sheet is the same height in both views (its header and two list rows), so switching doesn't move the map. */
  readonly nearbyViews: {
    readonly storageKey: string;
    readonly options: readonly {
      readonly id: string;
      readonly icon: string;
      readonly label: string;
    }[];
  };
  /** Every personal feature (docs/SPEC.md 'Two stages, two modes'): each control for one goes through the one account check per platform (web src/profile/account.ts requireAccount). In personal mode (Micah's private addresses, and the phone today) the check passes. In public mode (nowish.fyi, logged out) the controls still show, the same look, and tapping one shows copy account.comingSoon instead of acting; My events and Profile open to the same note. When sign-in arrives, that note becomes the sign-in prompt. */
  readonly accountFeatures: readonly string[];
  readonly fallbackEmoji: string;
  readonly seedInterests: readonly string[];
  /** Interests that describe how rather than what: alone they don't make an interest match or rescue a hidden event. */
  readonly broadInterests: readonly string[];
  /** Tags that no longer exist (the Free filter covers 'free events'): dropped from profiles, never stored. */
  readonly retiredTags: readonly string[];
  readonly copy: {
    readonly "search.placeholder": string;
    readonly "search.cancel": string;
    readonly "search.showResults": string;
    readonly "search.clear": string;
    readonly myEvents: string;
    readonly profile: string;
    readonly locate: string;
    readonly "locate.finding": string;
    readonly add: string;
    readonly "account.comingSoon": string;
    readonly close: string;
    readonly "filter.free": string;
    readonly "filter.anyPrice": string;
    readonly "filter.upTo": string;
    readonly "filter.priceHint": string;
    readonly "filter.anyType": string;
    readonly "filter.allInterests": string;
    readonly "filter.all": string;
    readonly "filter.reset": string;
    readonly "level.interest": string;
    readonly "level.hide": string;
    readonly "level.clear": string;
    readonly "level.menu": string;
    readonly "list.loading": string;
    readonly "list.oneHere": string;
    readonly "list.manyHere": string;
    readonly "list.oneResult": string;
    readonly "list.manyResults": string;
    readonly "list.noMatches": string;
    readonly "list.noMatchesHint": string;
    readonly "list.nothing": string;
    readonly "list.soonest": string;
    readonly "list.nothingHint": string;
    readonly "list.viewCards": string;
    readonly "list.viewList": string;
    readonly "card.interested": string;
    readonly "card.interestedOn": string;
    readonly "card.notInterested": string;
    readonly "card.directions": string;
    readonly "card.cancelled": string;
    readonly "card.soldOut": string;
    readonly "card.closed": string;
    readonly "card.noLongerListed": string;
    readonly "card.recurring": string;
    readonly "card.endUnknown": string;
    readonly "card.locationOnMap": string;
    readonly "card.userEvent": string;
    readonly "card.starEvent": string;
    readonly "card.unstarEvent": string;
    readonly "myEvents.emptyTitle": string;
    readonly "myEvents.emptyText": string;
    readonly "toast.hidden": string;
    readonly "toast.undo": string;
    readonly "toast.question": string;
    readonly "toast.allOfThese": string;
    readonly "free.label": string;
    readonly "free.howLong": string;
    readonly "profile.interests": string;
    readonly "profile.addInterest": string;
    readonly "profile.add": string;
    readonly "profile.interestsHint": string;
    readonly "profile.hidden": string;
    readonly "profile.hiddenHint": string;
    readonly "profile.unhide": string;
    readonly "profile.otherTags": string;
    readonly "profile.otherTagsHint": string;
    readonly "profile.reset": string;
    readonly "profile.resetConfirm": string;
  };
}

export type CopyKey =
  | "search.placeholder"
  | "search.cancel"
  | "search.showResults"
  | "search.clear"
  | "myEvents"
  | "profile"
  | "locate"
  | "locate.finding"
  | "add"
  | "account.comingSoon"
  | "close"
  | "filter.free"
  | "filter.anyPrice"
  | "filter.upTo"
  | "filter.priceHint"
  | "filter.anyType"
  | "filter.allInterests"
  | "filter.all"
  | "filter.reset"
  | "level.interest"
  | "level.hide"
  | "level.clear"
  | "level.menu"
  | "list.loading"
  | "list.oneHere"
  | "list.manyHere"
  | "list.oneResult"
  | "list.manyResults"
  | "list.noMatches"
  | "list.noMatchesHint"
  | "list.nothing"
  | "list.soonest"
  | "list.nothingHint"
  | "list.viewCards"
  | "list.viewList"
  | "card.interested"
  | "card.interestedOn"
  | "card.notInterested"
  | "card.directions"
  | "card.cancelled"
  | "card.soldOut"
  | "card.closed"
  | "card.noLongerListed"
  | "card.recurring"
  | "card.endUnknown"
  | "card.locationOnMap"
  | "card.userEvent"
  | "card.starEvent"
  | "card.unstarEvent"
  | "myEvents.emptyTitle"
  | "myEvents.emptyText"
  | "toast.hidden"
  | "toast.undo"
  | "toast.question"
  | "toast.allOfThese"
  | "free.label"
  | "free.howLong"
  | "profile.interests"
  | "profile.addInterest"
  | "profile.add"
  | "profile.interestsHint"
  | "profile.hidden"
  | "profile.hiddenHint"
  | "profile.unhide"
  | "profile.otherTags"
  | "profile.otherTagsHint"
  | "profile.reset"
  | "profile.resetConfirm";

export const SHARED: Shared = {
  "colors": {
    "accent": {
      "light": "#007AFF",
      "dark": "#0A84FF"
    },
    "accentFill": {
      "light": "#E5F2FF",
      "dark": "#0B2440"
    },
    "now": {
      "light": "#22C55E"
    },
    "nowFill": {
      "light": "#E6FFF4"
    },
    "nowText": {
      "light": "#15803D",
      "dark": "#22C55E"
    },
    "soon": {
      "light": "#1E92E6"
    },
    "soonFill": {
      "light": "#DEF7FC"
    },
    "soonText": {
      "light": "#0B6CB3",
      "dark": "#1E92E6"
    },
    "starred": {
      "light": "#FFAA00"
    },
    "selected": {
      "light": "#222222",
      "dark": "#EDEDED"
    },
    "text": {
      "light": "#222222",
      "dark": "#EDEDED"
    },
    "muted": {
      "light": "#6A6A6A",
      "dark": "#9A9A9A"
    },
    "fill": {
      "light": "#F7F7F7",
      "dark": "#2C2C2E"
    },
    "line": {
      "light": "#DDDDDD",
      "dark": "#3A3A3C"
    },
    "lineSoft": {
      "light": "#EBEBEB",
      "dark": "#2C2C2E"
    },
    "surface": {
      "light": "#FFFFFF",
      "dark": "#000000"
    },
    "onColor": {
      "light": "#FFFFFF"
    },
    "ink": {
      "light": "#1A1A1A"
    },
    "danger": {
      "light": "#D93025",
      "dark": "#FF6B5E"
    },
    "dangerFill": {
      "light": "#FCE8E6",
      "dark": "#3D1F1C"
    },
    "warning": {
      "light": "#B25E09",
      "dark": "#FF9F0A"
    }
  },
  "endOfDayHour": 3,
  "unknownEnd": {
    "hours": 6,
    "fadeAfterHours": 2,
    "minOpacity": 0.5
  },
  "tabs": [
    {
      "id": "today",
      "title": "Today",
      "empty": "today"
    },
    {
      "id": "weekdays",
      "title": "Weekdays",
      "empty": "on weekdays this week",
      "emptyAhead": "on weekdays next week",
      "week": {
        "from": 1,
        "to": 5,
        "currentUntil": 4
      }
    },
    {
      "id": "weekend",
      "title": "Weekend",
      "empty": "this weekend",
      "week": {
        "from": 5,
        "to": 7,
        "currentUntil": 7
      }
    },
    {
      "id": "month",
      "title": "Month",
      "empty": "in the next month",
      "days": 30
    }
  ],
  "tabOrder": [
    {
      "weekdays": [
        1,
        2,
        3
      ],
      "tabs": [
        "today",
        "weekdays",
        "weekend",
        "month"
      ]
    },
    {
      "weekdays": [
        4,
        5,
        6,
        7
      ],
      "tabs": [
        "today",
        "weekend",
        "weekdays",
        "month"
      ]
    }
  ],
  "freeLengths": [
    {
      "id": "oneHour",
      "title": "1 hour",
      "hours": 1
    },
    {
      "id": "threeHours",
      "title": "3 hours",
      "hours": 3
    },
    {
      "id": "endOfDay",
      "title": "End of day",
      "hours": null
    },
    {
      "id": "untilOff",
      "title": "Until I turn it off",
      "hours": null
    }
  ],
  "freeDefaultLength": "endOfDay",
  "durations": [
    {
      "title": "30 min",
      "minutes": 30
    },
    {
      "title": "1 hour",
      "minutes": 60
    },
    {
      "title": "2 hours",
      "minutes": 120
    },
    {
      "title": "3 hours",
      "minutes": 180
    },
    {
      "title": "4 hours",
      "minutes": 240
    },
    {
      "title": "Not sure",
      "minutes": null
    }
  ],
  "defaultDuration": 2,
  "resetIcon": "arrow-back-up",
  "anyTypeIcon": "types-grid",
  "price": {
    "ceiling": 100,
    "step": 5,
    "autoCloseMs": 300
  },
  "pins": {
    "size": {
      "interest": 44,
      "none": 29
    },
    "soonMinutes": 120,
    "outline": {
      "nowWidth": 2,
      "soonWidth": 2,
      "plainWidth": 1
    },
    "squareCorner": 0.28,
    "notchRadius": 0.14,
    "price": {
      "font": 9,
      "height": 12,
      "padX": 3,
      "overhang": 3,
      "color": "muted",
      "background": "surface"
    },
    "secondaryOpacity": 0.55,
    "pointer": {
      "radius": 14,
      "chevronGap": 4,
      "chevronSize": 4,
      "chevronWidth": 1.5,
      "chevronColor": {
        "now": "now",
        "soon": "soon",
        "plain": "muted"
      }
    },
    "tap": {
      "selectScale": 1.3,
      "springMs": 450,
      "popMs": 450
    }
  },
  "group": {
    "scale": 1.15,
    "maxEmojis": 3,
    "layout": [
      {
        "font": 24,
        "offsets": [
          [
            0,
            0
          ]
        ]
      },
      {
        "font": 19,
        "offsets": [
          [
            -7,
            -6
          ],
          [
            7,
            7
          ]
        ]
      },
      {
        "font": 16,
        "offsets": [
          [
            0,
            -9
          ],
          [
            -9,
            7
          ],
          [
            9,
            7
          ]
        ]
      }
    ]
  },
  "cardRadius": 16,
  "hover": {
    "pinScale": 1.15,
    "dimOthers": 0.7,
    "ms": 150
  },
  "maxPointers": 5,
  "pointerClearance": 7,
  "pointerFadeMs": 180,
  "chipOn": {
    "border": "accent",
    "fill": "accentFill",
    "text": "accent"
  },
  "iconInkDrop": {
    "free-gift": 1.5,
    "price-ticket": 1.5,
    "types-grid": 1.5
  },
  "fetch": {
    "marginFactor": 0.5,
    "minSpanDegrees": 0.25,
    "pastHours": 12,
    "aheadHours": 24,
    "pageLimit": 4000,
    "maxEvents": 30000,
    "refreshMinutes": 5,
    "fullMinutes": 60
  },
  "nearbyViews": {
    "storageKey": "nearbyView",
    "options": [
      {
        "id": "cards",
        "icon": "layout-rows",
        "label": "list.viewCards"
      },
      {
        "id": "list",
        "icon": "list",
        "label": "list.viewList"
      }
    ]
  },
  "accountFeatures": [
    "addEvent",
    "stars",
    "interested",
    "notInterested",
    "typeLevels",
    "myEvents",
    "profile",
    "free",
    "alerts"
  ],
  "fallbackEmoji": "📍",
  "seedInterests": [
    "kava",
    "biking",
    "art",
    "street art",
    "outdoor activities",
    "mischief",
    "edm",
    "burning man",
    "psychedelics",
    "flash mobs",
    "ice responses",
    "progressive politics",
    "dsa",
    "antifascism",
    "antizionism",
    "antiracism",
    "solidarity",
    "farmers markets",
    "dogs",
    "dog adoption",
    "nature",
    "open source",
    "makers",
    "hackathons",
    "weird",
    "aliens",
    "sci-fi",
    "immersive art",
    "collaboration",
    "community gardens",
    "resilience",
    "mutual aid",
    "design",
    "branding",
    "ux design",
    "sculpting"
  ],
  "broadInterests": [
    "outdoor activities"
  ],
  "retiredTags": [
    "free events"
  ],
  "copy": {
    "search.placeholder": "Search events, places, types",
    "search.cancel": "Cancel",
    "search.showResults": "Show results list",
    "search.clear": "Clear search",
    "myEvents": "My events",
    "profile": "Profile",
    "locate": "Show my location",
    "locate.finding": "Finding you…",
    "add": "Add event",
    "account.comingSoon": "Coming soon: sign-in",
    "close": "Close",
    "filter.free": "Free",
    "filter.anyPrice": "Price",
    "filter.upTo": "Up to {price}",
    "filter.priceHint": "Events with an unknown price still show.",
    "filter.anyType": "Types",
    "filter.allInterests": "All interests",
    "filter.all": "All",
    "filter.reset": "Reset",
    "level.interest": "Interested",
    "level.hide": "Hide",
    "level.clear": "Clear",
    "level.menu": "How do you feel about {tag}?",
    "list.loading": "Loading events…",
    "list.oneHere": "1 event here",
    "list.manyHere": "{n} events here",
    "list.oneResult": "1 result",
    "list.manyResults": "{n} results",
    "list.noMatches": "No matches",
    "list.noMatchesHint": "Try another tab or fewer filters.",
    "list.nothing": "Nothing here {phrase}",
    "list.soonest": "Soonest:",
    "list.nothingHint": "Try another tab, fewer filters, or zoom out.",
    "list.viewCards": "Cards",
    "list.viewList": "List",
    "card.interested": "I'm interested",
    "card.interestedOn": "Interested",
    "card.notInterested": "Not interested",
    "card.directions": "Directions",
    "card.cancelled": "Cancelled",
    "card.soldOut": "Sold out",
    "card.closed": "Sign-ups have closed",
    "card.noLongerListed": "No longer listed. It may be cancelled; check the source.",
    "card.recurring": "Happens regularly",
    "card.endUnknown": "End time unknown",
    "card.locationOnMap": "Location on map",
    "card.userEvent": "Added by a Nowish user",
    "card.starEvent": "Star event",
    "card.unstarEvent": "Unstar event",
    "myEvents.emptyTitle": "No starred events",
    "myEvents.emptyText": "Tap I'm interested on an event to follow it here and on your phone: you'll get a reminder 2 hours before and a heads-up if it changes.",
    "toast.hidden": "Hidden.",
    "toast.undo": "Undo",
    "toast.question": "Not into these?",
    "toast.allOfThese": "All of these",
    "free.label": "I'm free",
    "free.howLong": "How long",
    "profile.interests": "Your interests ({n})",
    "profile.addInterest": "Add an interest",
    "profile.add": "Add",
    "profile.interestsHint": "Star the types you're into. Interests show first in the type filter, get bigger pins and are highlighted on event cards.",
    "profile.hidden": "Hidden",
    "profile.hiddenHint": "Events with a hidden type stay off the map and out of alerts, unless they also match one of your interests. Tap to unhide.",
    "profile.unhide": "Unhide",
    "profile.otherTags": "Other types in use",
    "profile.otherTagsHint": "Tap to add to your interests.",
    "profile.reset": "Reset to starting interests",
    "profile.resetConfirm": "Replace your interests with the starting list?"
  }
};

/** UI copy by key; `{name}` placeholders filled from `args` (ios Shared.text, web boot.js T). */
export function T(key: CopyKey, args: Record<string, string | number> = {}): string {
  const copy: Record<string, string> = SHARED.copy;
  return Object.entries(args).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, String(v)), copy[key] ?? key);
}
